import { packedToTileRef } from "../document/packed-tile.ts";
import { COLLISION_CELLS_PER_TILE, type EntityInstance, type LayerDocument, type MapDocument, type PropInstance, type TileRef, type WorldDocument } from "../document/types.ts";

/**
 * A rectangle of one map as a reader sees it (ADR-099 decision 22): every painted layer as run-length rows over
 * a legend in the layer's own grid (a terrain layer's corners, a tile layer's tiles, a collision or zone layer's
 * collision cells), and the props and markers whose anchor lies inside, as the document holds them. The rows
 * decode back to exactly the document's values (`regionValues`), so a reader can be told the paint without
 * the document. Pure: no DOM, no clock.
 */

/**
 * The most tiles one view covers: 64 by 64.
 */
export const REGION_TILES_MAX = 4096;

/**
 * The most props and markers one view lists across its layers; the rest are counted.
 */
export const REGION_ITEMS_MAX = 500;

/**
 * A rectangle in tiles.
 */
export interface TileRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * One value of a painted grid: a terrain colour or a collision byte, a tile, a zone type's id, or null for an
 * empty tile or an unpainted zone cell.
 */
export type RegionValue = number | string | TileRef | null;

/**
 * One run of a row: the legend's index of its value and how many cells it covers.
 */
export type RegionRun = readonly [value: number, count: number];

/**
 * A painted layer in the view.
 */
export interface RegionGrid {
  readonly layer: string;
  readonly name: string;
  readonly kind: "terrain" | "tile" | "collision" | "zone" | "door";
  /** The grid the rows are in: corners (a tile's `w + 1` by `h + 1`), tiles, or collision cells. */
  readonly grid: "corners" | "tiles" | "collision" | "zone" | "door";
  /** The terrain a terrain layer paints. */
  readonly terrain?: string;
  readonly variant?: string;
  /** The rectangle in the grid's own units. */
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** Each distinct value once, in the order the rows first meet it. */
  readonly legend: readonly RegionValue[];
  /** Top row first; each row's runs cover exactly `w` cells. */
  readonly rows: readonly (readonly RegionRun[])[];
}

/**
 * A prop layer in the view: the props anchored inside, in the layer's order.
 */
export interface RegionProps {
  readonly layer: string;
  readonly name: string;
  readonly kind: "prop";
  readonly props: readonly PropInstance[];
}

/**
 * A marker layer in the view: the markers anchored inside, in the layer's order.
 */
export interface RegionEntities {
  readonly layer: string;
  readonly name: string;
  readonly kind: "entity";
  readonly entities: readonly EntityInstance[];
}

/**
 * One layer of the view.
 */
export type RegionLayer = RegionGrid | RegionProps | RegionEntities;

/**
 * The view.
 */
export interface RegionView {
  readonly map: string;
  readonly rect: TileRect;
  /** The layers asked for, in the map's order. */
  readonly layers: readonly RegionLayer[];
  /** Props and markers anchored inside that were not listed for `REGION_ITEMS_MAX`. */
  readonly left: number;
}

/**
 * The view, or why it was refused: `map` for a map the world lacks, `layer` for a layer the map lacks, `rect`
 * for a rectangle that is not whole tiles inside the map or covers more than `REGION_TILES_MAX`.
 */
export type RegionOutcome = { readonly ok: true; readonly view: RegionView } | { readonly ok: false; readonly error: "map" | "layer" | "rect"; readonly message: string };

/**
 * Builds one painted layer's rows.
 *
 * @param head The layer's id, name and kind fields.
 * @param stride The grid's width.
 * @param box The rectangle in the grid's units.
 * @param valueAt The value of one cell of the grid by its index.
 * @returns The grid.
 */
function gridOf(head: Pick<RegionGrid, "layer" | "name" | "kind" | "grid" | "terrain" | "variant">, stride: number, box: TileRect, valueAt: (index: number) => RegionValue): RegionGrid {
  const legend: RegionValue[] = [];
  const keys = new Map<string, number>();
  const rows: RegionRun[][] = [];
  for (let y = box.y; y < box.y + box.h; y += 1) {
    const row: [number, number][] = [];
    for (let x = box.x; x < box.x + box.w; x += 1) {
      const value = valueAt(y * stride + x);
      const key = JSON.stringify(value);
      let index = keys.get(key);
      if (index === undefined) {
        index = legend.length;
        legend.push(value);
        keys.set(key, index);
      }
      const last = row[row.length - 1];
      if (last !== undefined && last[0] === index) {
        last[1] += 1;
      } else {
        row.push([index, 1]);
      }
    }
    rows.push(row);
  }
  return { ...head, x: box.x, y: box.y, w: box.w, h: box.h, legend, rows };
}

/**
 * Whether an anchor lies inside a rectangle of tiles.
 *
 * @param rect The rectangle.
 * @param x The anchor's column, in tiles.
 * @param y The anchor's row, in tiles.
 * @returns True when `rect.x <= x < rect.x + rect.w` and likewise for y.
 */
function inside(rect: TileRect, x: number, y: number): boolean {
  return x >= rect.x && x < rect.x + rect.w && y >= rect.y && y < rect.y + rect.h;
}

/**
 * One layer of the view.
 *
 * @param document The world, for its tilesets.
 * @param map The map.
 * @param layer The layer.
 * @param rect The rectangle in tiles.
 * @param room How many more items may be listed.
 * @returns The layer and how many items it anchors inside.
 */
function layerView(document: WorldDocument, map: MapDocument, layer: LayerDocument, rect: TileRect, room: number): { readonly view: RegionLayer; readonly items: number } {
  const head = { layer: layer.id, name: layer.name };
  const cells = { x: rect.x * COLLISION_CELLS_PER_TILE, y: rect.y * COLLISION_CELLS_PER_TILE, w: rect.w * COLLISION_CELLS_PER_TILE, h: rect.h * COLLISION_CELLS_PER_TILE };
  const cellStride = map.width * COLLISION_CELLS_PER_TILE;
  switch (layer.kind) {
    case "terrain":
      return { view: gridOf({ ...head, kind: "terrain", grid: "corners", terrain: layer.terrain, variant: layer.variant }, map.width + 1, { x: rect.x, y: rect.y, w: rect.w + 1, h: rect.h + 1 }, (i) => layer.corners[i] as number), items: 0 };
    case "tile":
      return { view: gridOf({ ...head, kind: "tile", grid: "tiles" }, map.width, rect, (i) => packedToTileRef(layer.tiles[i] as number, document.tilesets)), items: 0 };
    case "collision":
      return { view: gridOf({ ...head, kind: "collision", grid: "collision" }, cellStride, cells, (i) => layer.cells[i] as number), items: 0 };
    case "zone":
      return { view: gridOf({ ...head, kind: "zone", grid: "zone" }, cellStride, cells, (i) => (layer.cells[i] === 0 ? null : (layer.types[(layer.cells[i] as number) - 1] ?? null))), items: 0 };
    case "door":
      return { view: gridOf({ ...head, kind: "door", grid: "door" }, cellStride, cells, (i) => (layer.cells[i] === 0 ? null : (layer.doors[(layer.cells[i] as number) - 1]?.id ?? null))), items: 0 };
    case "prop": {
      const found = layer.props.filter((prop) => inside(rect, prop.x, prop.y));
      return { view: { ...head, kind: "prop", props: found.slice(0, room) }, items: found.length };
    }
    case "entity": {
      const found = layer.entities.filter((entity) => inside(rect, entity.x, entity.y));
      return { view: { ...head, kind: "entity", entities: found.slice(0, room) }, items: found.length };
    }
  }
}

/**
 * Views a rectangle of one map.
 *
 * @param document The world.
 * @param mapId The map.
 * @param rect The rectangle, whole tiles inside the map, at most `REGION_TILES_MAX` of them.
 * @param layers The layers to show by id, every layer of the map when absent.
 * @returns The view, or why it was refused.
 */
export function regionView(document: WorldDocument, mapId: string, rect: TileRect, layers?: readonly string[]): RegionOutcome {
  const map = document.maps.find((candidate) => candidate.id === mapId);
  if (map === undefined) {
    return { ok: false, error: "map", message: `the world holds no map ${mapId}` };
  }
  const { x, y, w, h } = rect;
  const whole = [x, y, w, h].every((value) => Number.isSafeInteger(value));
  if (!whole || x < 0 || y < 0 || w < 1 || h < 1 || x + w > map.width || y + h > map.height) {
    return { ok: false, error: "rect", message: `the rectangle must be whole tiles inside the map's ${map.width} by ${map.height}` };
  }
  if (w * h > REGION_TILES_MAX) {
    return { ok: false, error: "rect", message: `the rectangle covers ${w * h} tiles; at most ${REGION_TILES_MAX} (64 by 64) a call` };
  }
  const missing = (layers ?? []).find((id) => !map.layers.some((layer) => layer.id === id));
  if (missing !== undefined) {
    return { ok: false, error: "layer", message: `the map holds no layer ${missing}` };
  }
  const chosen = layers === undefined ? map.layers : map.layers.filter((layer) => layers.includes(layer.id));
  const views: RegionLayer[] = [];
  let listed = 0;
  let anchored = 0;
  for (const layer of chosen) {
    const { view, items } = layerView(document, map, layer, { x, y, w, h }, REGION_ITEMS_MAX - listed);
    views.push(view);
    anchored += items;
    listed = Math.min(REGION_ITEMS_MAX, listed + items);
  }
  return { ok: true, view: { map: map.id, rect: { x, y, w, h }, layers: views, left: anchored - listed } };
}

/**
 * Decodes a painted layer's rows back to its values, row-major over its rectangle.
 *
 * @param grid The layer as the view gives it.
 * @returns The values.
 * @throws RangeError when a run names a value the legend lacks or a row does not cover `w` cells.
 */
export function regionValues(grid: RegionGrid): RegionValue[] {
  const out: RegionValue[] = [];
  for (const row of grid.rows) {
    let covered = 0;
    for (const [index, count] of row) {
      if (index < 0 || index >= grid.legend.length) {
        throw new RangeError(`run names legend entry ${index} of ${grid.legend.length}`);
      }
      for (let i = 0; i < count; i += 1) {
        out.push(grid.legend[index] as RegionValue);
      }
      covered += count;
    }
    if (covered !== grid.w) {
      throw new RangeError(`a row covers ${covered} cells of ${grid.w}`);
    }
  }
  return out;
}
